<script setup>
import { computed, onMounted, onUnmounted, ref, watch, nextTick } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useResizeObserver } from '@vueuse/core';
import DropdownMenu from '@/dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  columns: {
    type: Array,
    required: true,
    // Expected format: [{ field: string, headerName: string, editable: boolean, width?: number }]
  },
  rows: {
    type: Array,
    default: () => [],
  },
  visibleColumns: {
    type: Array,
    default: () => [],
    // If empty, all columns are visible
  },
  cellHeight: {
    type: Number,
    default: 48,
  },
  defaultColumnWidth: {
    type: Number,
    default: 180,
  },
  minColumnWidth: {
    type: Number,
    default: 60,
  },
  enableRowSelection: {
    type: Boolean,
    default: true,
  },
  enableCellSelection: {
    type: Boolean,
    default: true,
  },
  enableColumnReorder: {
    type: Boolean,
    default: true,
  },
  enableColumnResize: {
    type: Boolean,
    default: true,
  },
  enableEditing: {
    type: Boolean,
    default: true,
  },
  fillHeight: {
    type: Boolean,
    default: false,
  },
  activeFilters: {
    type: Object,
    default: () => ({}),
  },
  selectedRows: {
    type: Array,
    default: () => [],
  },
  formatCellValue: {
    type: Function,
    default: value => {
      if (value === null || value === undefined) return '';
      return String(value);
    },
  },
  isCellEditable: {
    type: Function,
    default: () => true,
  },
  getCellType: {
    type: Function,
    default: () => 'string',
  },
});

const emit = defineEmits([
  'update:selectedRows',
  'cellEdit',
  'rowClick',
  'cellClick',
  'columnReorder',
  'columnResize',
  'columnShow',
  'columnHide',
  'copyValues',
  'columnDragStart',
  'columnDragEnd',
  'columnFilter',
]);

const containerRef = ref(null);
const containerHeight = ref(0);
const headerHeight = 37; // approximate sticky header height

useResizeObserver(containerRef, entries => {
  if (entries[0]) {
    containerHeight.value = entries[0].contentRect.height;
  }
});

const CELL_HEIGHT = computed(() => {
  if (!props.fillHeight || props.rows.length === 0) return props.cellHeight;
  const available = containerHeight.value - headerHeight;
  if (available <= 0) return props.cellHeight;
  const perRow = Math.floor(available / props.rows.length);
  // Clamp between default and 120px max
  return Math.max(props.cellHeight, Math.min(120, perRow));
});

const filterPopoverColumn = ref(null);
const filterInputValue = ref('');

const openFilterPopover = field => {
  filterPopoverColumn.value = field;
  filterInputValue.value = props.activeFilters[field]?.value || '';
  nextTick(() => {
    const input = document.querySelector('.data-grid-filter-input');
    if (input) input.focus();
  });
};

const closeFilterPopover = () => {
  filterPopoverColumn.value = null;
  filterInputValue.value = '';
};

const applyFilter = field => {
  const value = filterInputValue.value.trim();
  if (value) {
    emit('columnFilter', { field, value, operator: 'contains' });
  } else {
    emit('columnFilter', { field, value: null, operator: null });
  }
  closeFilterPopover();
};

const clearFilter = field => {
  emit('columnFilter', { field, value: null, operator: null });
  closeFilterPopover();
};

const editingCell = ref(null);
const editingValue = ref('');
const selectedRowIds = ref(new Set(props.selectedRows.map(r => r.id || r)));
const columnWidths = ref({});
const resizingColumn = ref(null);
const resizeStartX = ref(0);
const resizeStartWidth = ref(0);
const draggingColumn = ref(null);
const dragOverColumn = ref(null);
const showColumnOptionsDropdown = ref(null);
const selectedCells = ref(new Set());
const columnOrder = ref([]);
const activeCell = ref(null);
const cellEditableCache = ref(new Map());
const cellWidthCache = ref(new Map());

const getCellType = field => {
  const type = props.getCellType(field);
  // Debug: log the type for troubleshooting
  // console.log(`getCellType for ${field}:`, type);
  return type;
};

const getCellWidth = field => {
  if (cellWidthCache.value.has(field)) {
    return cellWidthCache.value.get(field);
  }

  let width;
  if (columnWidths.value[field]) {
    width = `${columnWidths.value[field]}px`;
  } else {
    const col = props.columns.find(c => c.field === field);
    if (col?.width) {
      width = `${col.width}px`;
    } else {
      width = `${props.defaultColumnWidth}px`;
    }
  }

  cellWidthCache.value.set(field, width);
  return width;
};

const getNumericWidth = field => {
  if (columnWidths.value[field]) {
    return columnWidths.value[field];
  }
  const col = props.columns.find(c => c.field === field);
  if (col?.width) {
    return col.width;
  }
  return props.defaultColumnWidth;
};

const startResize = (field, event) => {
  if (!props.enableColumnResize) return;

  event.preventDefault();
  resizingColumn.value = field;
  resizeStartX.value = event.clientX;
  resizeStartWidth.value = getNumericWidth(field);

  document.addEventListener('mousemove', handleResize);
  document.addEventListener('mouseup', stopResize);
};

const handleResize = event => {
  if (!resizingColumn.value) return;

  const delta = event.clientX - resizeStartX.value;
  const newWidth = Math.max(
    props.minColumnWidth,
    resizeStartWidth.value + delta
  );

  columnWidths.value = {
    ...columnWidths.value,
    [resizingColumn.value]: newWidth,
  };

  // Clear width cache for resized column
  cellWidthCache.value.delete(resizingColumn.value);

  emit('columnResize', { field: resizingColumn.value, width: newWidth });
};

const stopResize = () => {
  resizingColumn.value = null;
  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mouseup', stopResize);
};

const startDrag = (field, event) => {
  if (!props.enableColumnReorder) return;

  event.stopPropagation();
  draggingColumn.value = field;
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('application/x-column-drag', field);
  event.dataTransfer.setData('text/plain', '');
  emit('columnDragStart');
};

const handleDragOver = (field, event) => {
  if (!props.enableColumnReorder) return;

  event.preventDefault();
  event.stopPropagation();
  event.dataTransfer.dropEffect = 'move';
  dragOverColumn.value = field;
};

const handleDragLeave = () => {
  dragOverColumn.value = null;
};

const handleDrop = (targetField, event) => {
  if (!props.enableColumnReorder) return;

  event.preventDefault();
  event.stopPropagation();

  const sourceField = draggingColumn.value;
  if (!sourceField || sourceField === targetField) {
    draggingColumn.value = null;
    dragOverColumn.value = null;
    return;
  }

  const currentOrder = [...columnOrder.value];
  const sourceIndex = currentOrder.findIndex(col => col.field === sourceField);
  const targetIndex = currentOrder.findIndex(col => col.field === targetField);

  if (sourceIndex !== -1 && targetIndex !== -1) {
    const [removed] = currentOrder.splice(sourceIndex, 1);
    currentOrder.splice(targetIndex, 0, removed);
    columnOrder.value = currentOrder;

    emit('columnReorder', { sourceField, targetField, newOrder: currentOrder });
  }

  draggingColumn.value = null;
  dragOverColumn.value = null;
  emit('columnDragEnd');
};

const handleDragEnd = () => {
  draggingColumn.value = null;
  dragOverColumn.value = null;
  emit('columnDragEnd');
};

const toggleRowSelection = rowId => {
  if (!props.enableRowSelection) return;

  if (selectedRowIds.value.has(rowId)) {
    selectedRowIds.value.delete(rowId);
  } else {
    selectedRowIds.value.add(rowId);
  }

  const selected = props.rows.filter(row => selectedRowIds.value.has(row.id));
  emit('update:selectedRows', selected);
};

const toggleSelectAll = () => {
  if (!props.enableRowSelection) return;

  if (isAllSelected.value) {
    selectedRowIds.value.clear();
  } else {
    props.rows.forEach(row => {
      selectedRowIds.value.add(row.id);
    });
  }

  const selected = props.rows.filter(row => selectedRowIds.value.has(row.id));
  emit('update:selectedRows', selected);
};

const isRowSelected = rowId => selectedRowIds.value.has(rowId);

const isAllSelected = computed(() => {
  if (props.rows.length === 0) return false;
  return props.rows.every(row => selectedRowIds.value.has(row.id));
});

const isSomeSelected = computed(() => {
  return selectedRowIds.value.size > 0 && !isAllSelected.value;
});

const startEditing = (rowId, field, currentValue) => {
  if (!props.enableEditing) return;

  const col = props.columns.find(c => c.field === field);
  if (!col?.editable || !props.isCellEditable(rowId, field)) return;

  const cellType = props.getCellType(field);
  const complexTypes = ['geo', 'object', 'array'];
  if (complexTypes.includes(cellType)) return;

  editingCell.value = { rowId, field };
  // Convert boolean values to string for the select input
  if (cellType === 'bool' || cellType === 'boolean') {
    editingValue.value = String(currentValue);
  } else {
    editingValue.value = currentValue || '';
  }
};

const cancelEditing = () => {
  editingCell.value = null;
  editingValue.value = '';
};

const saveEdit = () => {
  if (!editingCell.value) return;

  const { rowId, field } = editingCell.value;
  const row = props.rows.find(r => r.id === rowId);
  if (!row) return;

  const oldValue = row[field];
  const newValue = editingValue.value;

  if (newValue === oldValue) {
    cancelEditing();
    return;
  }

  emit('cellEdit', { rowId, field, oldValue, newValue });
  cancelEditing();
};

const toggleCellSelection = (rowId, field, event) => {
  if (!props.enableCellSelection) return;

  const cellKey = `${rowId}::${field}`;
  activeCell.value = { rowId, field };

  if (event.ctrlKey || event.metaKey) {
    if (selectedCells.value.has(cellKey)) {
      selectedCells.value.delete(cellKey);
    } else {
      selectedCells.value.add(cellKey);
    }
  } else {
    selectedCells.value.clear();
    selectedCells.value.add(cellKey);
  }
};

const isCellSelected = (rowId, field) => {
  const cellKey = `${rowId}::${field}`;
  return selectedCells.value.has(cellKey);
};

const isCellActive = (rowId, field) => {
  return activeCell.value?.rowId === rowId && activeCell.value?.field === field;
};

const getCachedCellEditable = (rowId, field) => {
  const cacheKey = `${rowId}::${field}`;
  if (!cellEditableCache.value.has(cacheKey)) {
    const col = props.columns.find(c => c.field === field);
    const result = col?.editable && props.isCellEditable(rowId, field);
    cellEditableCache.value.set(cacheKey, result);
  }
  return cellEditableCache.value.get(cacheKey);
};

const copySelectedValues = async () => {
  if (selectedCells.value.size === 0) return;

  const values = [];
  selectedCells.value.forEach(cellKey => {
    const [rowId, field] = cellKey.split('::');
    const row = props.rows.find(r => r.id === rowId);
    if (row) {
      values.push(props.formatCellValue(row[field], field));
    }
  });

  const text = values.join('\n');

  try {
    await navigator.clipboard.writeText(text);
    emit('copyValues', values);
  } catch (err) {
    console.error('Failed to copy:', err);
  }
};

const toggleColumnOptionsDropdown = field => {
  showColumnOptionsDropdown.value =
    showColumnOptionsDropdown.value === field ? null : field;
};

const getColumnOptionsMenuItems = field => {
  const colIndex = filteredColumns.value.findIndex(col => col.field === field);
  const canMoveLeft = colIndex > 0;
  const canMoveRight = colIndex < filteredColumns.value.length - 1;

  return [
    {
      label: 'Select Values',
      value: 'select-values',
      action: 'select-values',
      icon: 'i-lucide-check-square',
    },
    {
      label: 'Hide Field',
      value: 'hide',
      action: 'hide',
      icon: 'i-lucide-eye-off',
    },
    {
      label: 'Move Left',
      value: 'move-left',
      action: 'move-left',
      icon: 'i-lucide-arrow-left',
      disabled: !canMoveLeft,
    },
    {
      label: 'Move Right',
      value: 'move-right',
      action: 'move-right',
      icon: 'i-lucide-arrow-right',
      disabled: !canMoveRight,
    },
  ];
};

const handleColumnOptionAction = (field, { action }) => {
  showColumnOptionsDropdown.value = null;

  if (action === 'hide') {
    emit('columnHide', field);
  } else if (action === 'move-left') {
    const currentOrder = [...columnOrder.value];
    const index = currentOrder.findIndex(col => col.field === field);
    if (index > 0) {
      const [removed] = currentOrder.splice(index, 1);
      currentOrder.splice(index - 1, 0, removed);
      columnOrder.value = currentOrder;
      emit('columnReorder', { sourceField: field, newOrder: currentOrder });
    }
  } else if (action === 'move-right') {
    const currentOrder = [...columnOrder.value];
    const index = currentOrder.findIndex(col => col.field === field);
    if (index < currentOrder.length - 1) {
      const [removed] = currentOrder.splice(index, 1);
      currentOrder.splice(index + 1, 0, removed);
      columnOrder.value = currentOrder;
      emit('columnReorder', { sourceField: field, newOrder: currentOrder });
    }
  } else if (action === 'select-values') {
    selectedCells.value.clear();
    props.rows.forEach(row => {
      const cellKey = `${row.id}::${field}`;
      selectedCells.value.add(cellKey);
    });
  }
};

const filteredColumns = computed(() => {
  let cols = columnOrder.value.length > 0 ? columnOrder.value : props.columns;

  if (props.visibleColumns.length > 0) {
    cols = cols.filter(col => props.visibleColumns.includes(col.field));
  }

  return cols;
});

const isInputOrEditable = el => {
  if (!el || typeof el.closest !== 'function') return false;
  const tag = el.tagName?.toUpperCase?.();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return el.isContentEditable;
};

const handleKeyDown = event => {
  // Don't capture keys when user is typing in an input/textarea (e.g. modal form)
  if (isInputOrEditable(event.target)) return;

  // Escape: clear selection or cancel editing
  if (event.key === 'Escape') {
    if (editingCell.value) {
      cancelEditing();
      event.preventDefault();
    } else if (selectedCells.value.size > 0) {
      selectedCells.value.clear();
    }
    return;
  }

  // Copy selected values
  if (
    (event.ctrlKey || event.metaKey) &&
    event.key === 'c' &&
    selectedCells.value.size > 0 &&
    !editingCell.value
  ) {
    event.preventDefault();
    copySelectedValues();
    return;
  }

  // Select all
  if (
    (event.ctrlKey || event.metaKey) &&
    event.key === 'a' &&
    !editingCell.value
  ) {
    event.preventDefault();
    selectedCells.value.clear();
    props.rows.forEach(row => {
      filteredColumns.value.forEach(col => {
        const cellKey = `${row.id}::${col.field}`;
        selectedCells.value.add(cellKey);
      });
    });
    return;
  }

  // Don't handle navigation if editing
  if (editingCell.value) return;

  // Arrow keys, Tab, Enter, Home, End navigation
  if (
    [
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Enter',
      'Home',
      'End',
      'PageUp',
      'PageDown',
      'Delete',
      'Backspace',
    ].includes(event.key)
  ) {
    handleCellNavigation(event);
  }
};

const handleCellNavigation = event => {
  if (
    !activeCell.value &&
    props.rows.length > 0 &&
    filteredColumns.value.length > 0
  ) {
    // Initialize active cell to first cell
    activeCell.value = {
      rowId: props.rows[0].id,
      field: filteredColumns.value[0].field,
    };
    return;
  }

  if (!activeCell.value) return;

  const { rowId, field } = activeCell.value;
  const rowIndex = props.rows.findIndex(r => r.id === rowId);
  const colIndex = filteredColumns.value.findIndex(c => c.field === field);

  if (rowIndex === -1 || colIndex === -1) return;

  let newRowIndex = rowIndex;
  let newColIndex = colIndex;
  let shouldStartEditing = false;

  switch (event.key) {
    case 'ArrowUp':
      event.preventDefault();
      newRowIndex = Math.max(0, rowIndex - 1);
      break;

    case 'ArrowDown':
      event.preventDefault();
      newRowIndex = Math.min(props.rows.length - 1, rowIndex + 1);
      break;

    case 'ArrowLeft':
      event.preventDefault();
      newColIndex = Math.max(0, colIndex - 1);
      break;

    case 'ArrowRight':
      event.preventDefault();
      newColIndex = Math.min(filteredColumns.value.length - 1, colIndex + 1);
      break;

    case 'Tab':
      event.preventDefault();
      if (event.shiftKey) {
        // Move backward
        newColIndex--;
        if (newColIndex < 0) {
          newRowIndex--;
          newColIndex = filteredColumns.value.length - 1;
        }
        if (newRowIndex < 0) {
          newRowIndex = props.rows.length - 1;
        }
      } else {
        // Move forward
        newColIndex++;
        if (newColIndex >= filteredColumns.value.length) {
          newRowIndex++;
          newColIndex = 0;
        }
        if (newRowIndex >= props.rows.length) {
          newRowIndex = 0;
        }
      }
      break;

    case 'Enter':
      event.preventDefault();
      shouldStartEditing = true;
      break;

    case 'Home':
      event.preventDefault();
      if (event.ctrlKey || event.metaKey) {
        // Go to first cell
        newRowIndex = 0;
        newColIndex = 0;
      } else {
        // Go to first column in current row
        newColIndex = 0;
      }
      break;

    case 'End':
      event.preventDefault();
      if (event.ctrlKey || event.metaKey) {
        // Go to last cell
        newRowIndex = props.rows.length - 1;
        newColIndex = filteredColumns.value.length - 1;
      } else {
        // Go to last column in current row
        newColIndex = filteredColumns.value.length - 1;
      }
      break;

    case 'PageUp':
      event.preventDefault();
      newRowIndex = Math.max(0, rowIndex - 10);
      break;

    case 'PageDown':
      event.preventDefault();
      newRowIndex = Math.min(props.rows.length - 1, rowIndex + 10);
      break;

    case 'Delete':
    case 'Backspace':
      event.preventDefault();
      if (props.enableEditing) {
        const row = props.rows[rowIndex];
        const col = filteredColumns.value[colIndex];
        if (col.editable && props.isCellEditable(row.id, col.field)) {
          emit('cellEdit', {
            rowId: row.id,
            field: col.field,
            oldValue: row[col.field],
            newValue: '',
          });
        }
      }
      return;

    default:
      return;
  }

  // Bounds checking
  newRowIndex = Math.max(0, Math.min(props.rows.length - 1, newRowIndex));
  newColIndex = Math.max(
    0,
    Math.min(filteredColumns.value.length - 1, newColIndex)
  );

  if (newRowIndex !== rowIndex || newColIndex !== colIndex) {
    const newRow = props.rows[newRowIndex];
    const newCol = filteredColumns.value[newColIndex];
    activeCell.value = { rowId: newRow.id, field: newCol.field };

    // Scroll the cell into view
    scrollCellIntoView(newRow.id, newCol.field);
  }

  if (shouldStartEditing && activeCell.value) {
    const row = props.rows.find(r => r.id === activeCell.value.rowId);
    if (row) {
      startEditing(
        activeCell.value.rowId,
        activeCell.value.field,
        row[activeCell.value.field]
      );
    }
  }
};

const scrollCellIntoView = (rowId, field) => {
  // Use nextTick to ensure DOM is updated
  setTimeout(() => {
    const cellElement = document.querySelector(
      `[data-cell-id="${rowId}::${field}"]`
    );
    if (cellElement) {
      cellElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, 0);
};

onMounted(() => {
  columnOrder.value = [...props.columns];
  document.addEventListener('keydown', handleKeyDown);
});

watch(
  () => [props.columns, props.rows],
  () => {
    cellEditableCache.value.clear();
    cellWidthCache.value.clear();
  },
  { deep: false }
);

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown);
  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mouseup', stopResize);
});

defineExpose({
  clearSelection: () => {
    selectedRowIds.value.clear();
    selectedCells.value.clear();
    activeCell.value = null;
  },
  selectRows: rowIds => {
    selectedRowIds.value = new Set(rowIds);
  },
  setActiveCell: (rowId, field) => {
    activeCell.value = { rowId, field };
  },
});
</script>

<template>
  <div
    ref="containerRef"
    class="flex flex-col flex-1 min-h-0 min-w-0 overflow-auto bg-slate-25"
    style="will-change: scroll-position; overscroll-behavior: contain"
  >
    <div class="min-w-max bg-white" style="contain: layout style paint">
      <!-- Table Header -->
      <div
        class="sticky top-0 z-10 bg-slate-50 border-b border-slate-200"
        style="will-change: transform"
        role="row"
      >
        <div class="flex min-w-max">
          <!-- Checkbox Column Header -->
          <div
            v-if="enableRowSelection"
            role="columnheader"
            class="inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-slate-700 border-r border-slate-200 relative flex-shrink-0"
            style="width: 48px; min-width: 48px; max-width: 48px"
          >
            <input
              type="checkbox"
              :checked="isAllSelected"
              :indeterminate="isSomeSelected"
              class="rounded border-slate-300 text-woot-600 focus:ring-woot-500"
              @click.stop="toggleSelectAll"
            />
          </div>

          <!-- Column Headers -->
          <div
            v-for="col in filteredColumns"
            :key="col.field"
            role="columnheader"
            :draggable="enableColumnReorder"
            class="inline-flex items-center px-4 py-2 text-xs font-medium text-slate-700 border-r border-slate-200 last:border-r-0 relative flex-shrink-0"
            :class="{
              'cursor-move': enableColumnReorder,
              'opacity-50': draggingColumn === col.field,
              'bg-woot-50':
                dragOverColumn === col.field && draggingColumn !== col.field,
            }"
            :style="{
              width: getCellWidth(col.field),
              minWidth: getCellWidth(col.field),
              maxWidth: getCellWidth(col.field),
            }"
            @dragstart="startDrag(col.field, $event)"
            @dragover="handleDragOver(col.field, $event)"
            @dragleave="handleDragLeave"
            @drop="handleDrop(col.field, $event)"
            @dragend="handleDragEnd"
          >
            <!-- Drop Indicator -->
            <div
              v-if="
                dragOverColumn === col.field && draggingColumn !== col.field
              "
              class="absolute left-0 top-0 bottom-0 w-1 bg-woot-500 z-20 shadow-lg"
            />

            <span class="flex-1 truncate font-bold">{{ col.headerName }}</span>

            <!-- Filter Button -->
            <button
              type="button"
              class="p-1 ml-1 rounded hover:bg-woot-100 transition-colors"
              :class="{
                'bg-woot-100 text-woot-600': activeFilters[col.field]?.value,
              }"
              @click.stop="openFilterPopover(col.field)"
            >
              <span
                class="i-lucide-filter w-3 h-3"
                :class="
                  activeFilters[col.field]?.value
                    ? 'text-woot-600'
                    : 'text-slate-400'
                "
              />
            </button>

            <!-- Filter Popover -->
            <div
              v-if="filterPopoverColumn === col.field"
              class="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg p-3 min-w-[200px]"
              @click.stop
            >
              <OnClickOutside @trigger="closeFilterPopover">
                <div class="flex flex-col gap-2">
                  <label class="text-xs font-medium text-slate-600">
                    Filter {{ col.headerName }}
                  </label>
                  <input
                    v-model="filterInputValue"
                    type="text"
                    class="data-grid-filter-input reset-base w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-woot-500"
                    :placeholder="`Filter by ${col.headerName}...`"
                    @keydown.enter="applyFilter(col.field)"
                    @keydown.escape="closeFilterPopover"
                  />
                  <div class="flex gap-2 justify-end">
                    <button
                      v-if="activeFilters[col.field]?.value"
                      type="button"
                      class="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                      @click="clearFilter(col.field)"
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      class="px-2 py-1 text-xs text-woot-600 hover:bg-woot-50 rounded font-medium"
                      @click="applyFilter(col.field)"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </OnClickOutside>
            </div>

            <!-- Options Button -->
            <div class="relative flex-shrink-0">
              <OnClickOutside
                @trigger="
                  showColumnOptionsDropdown === col.field &&
                    (showColumnOptionsDropdown = null)
                "
              >
                <button
                  type="button"
                  class="p-1 ml-1 rounded hover:bg-woot-100 transition-colors"
                  :class="{
                    'bg-woot-100': showColumnOptionsDropdown === col.field,
                  }"
                  @click.stop="toggleColumnOptionsDropdown(col.field)"
                >
                  <span
                    class="i-lucide-more-vertical w-3.5 h-3.5 text-slate-500"
                  />
                </button>

                <div
                  v-if="showColumnOptionsDropdown === col.field"
                  class="absolute top-full left-0 mt-1 z-50"
                >
                  <DropdownMenu
                    :menu-items="getColumnOptionsMenuItems(col.field)"
                    @action="handleColumnOptionAction(col.field, $event)"
                  />
                </div>
              </OnClickOutside>
            </div>

            <!-- Resize Handle -->
            <div
              v-if="enableColumnResize"
              class="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-woot-400 transition-colors z-10"
              :class="{ 'bg-woot-500': resizingColumn === col.field }"
              @mousedown.stop="startResize(col.field, $event)"
            />
          </div>

          <!-- Header Slot for Additional Content -->
          <div
            role="columnheader"
            class="inline-flex items-center px-4 py-2 text-xs font-medium text-slate-700 flex-grow min-w-[120px] relative"
          >
            <slot name="header-actions" />
          </div>
        </div>
      </div>

      <!-- Table Body -->
      <div class="divide-y divide-slate-100 border-b border-slate-100">
        <div
          v-for="(row, rowIndex) in rows"
          :key="row.id"
          role="row"
          class="flex hover:bg-slate-75 transition-colors w-full"
          :class="{ 'bg-woot-50': isRowSelected(row.id) }"
          @click="emit('rowClick', row)"
        >
          <!-- Checkbox Column -->
          <div
            v-if="enableRowSelection"
            role="gridcell"
            class="inline-flex items-center justify-center border-r border-slate-100 flex-shrink-0"
            :style="{
              height: `${CELL_HEIGHT}px`,
              width: '48px',
              minWidth: '48px',
              maxWidth: '48px',
            }"
            @click.stop
          >
            <input
              type="checkbox"
              :checked="isRowSelected(row.id)"
              class="rounded border-slate-300 text-woot-600 focus:ring-woot-500"
              @click.stop="toggleRowSelection(row.id)"
            />
          </div>

          <!-- Data Cells -->
          <div
            v-for="col in filteredColumns"
            :key="`${row.id}-${col.field}`"
            role="gridcell"
            :data-cell-id="`${row.id}::${col.field}`"
            tabindex="-1"
            class="inline-flex items-center px-4 text-sm border-r border-slate-100 last:border-r-0 flex-shrink-0 select-none"
            :class="[
              getCachedCellEditable(row.id, col.field) && enableEditing
                ? 'cursor-pointer hover:bg-slate-100'
                : 'text-slate-500 cursor-not-allowed',
              isCellSelected(row.id, col.field)
                ? 'bg-woot-200 border-2 border-woot-500'
                : '',
              isCellActive(row.id, col.field) &&
              !isCellSelected(row.id, col.field)
                ? 'ring-2 ring-woot-600 ring-inset'
                : '',
            ]"
            :style="{
              height: `${CELL_HEIGHT}px`,
              width: getCellWidth(col.field),
              minWidth: getCellWidth(col.field),
              maxWidth: getCellWidth(col.field),
            }"
            @dblclick="startEditing(row.id, col.field, row[col.field])"
            @click="
              toggleCellSelection(row.id, col.field, $event);
              emit('cellClick', { row, field: col.field });
            "
          >
            <!-- Cell Slot (allows custom cell rendering) -->
            <slot
              name="cell"
              :row="row"
              :field="col.field"
              :value="row[col.field]"
              :editing="
                editingCell?.rowId === row.id &&
                editingCell?.field === col.field
              "
            >
              <!-- Editing Input for Boolean -->
              <select
                v-if="
                  editingCell?.rowId === row.id &&
                  editingCell?.field === col.field &&
                  (getCellType(col.field) === 'bool' ||
                    getCellType(col.field) === 'boolean')
                "
                v-model="editingValue"
                class="reset-base mb-0 w-full px-2 py-1 text-sm bg-white border border-woot-500 rounded focus:outline-none focus:ring-1 focus:ring-woot-500"
                @change="saveEdit"
                @blur="saveEdit"
                @keydown.escape="cancelEditing"
                @click.stop
              >
                <option value="true">true</option>
                <option value="false">false</option>
              </select>

              <!-- Editing Input for String/Keyword -->
              <input
                v-else-if="
                  editingCell?.rowId === row.id &&
                  editingCell?.field === col.field
                "
                v-model="editingValue"
                type="text"
                class="reset-base w-full px-0 py-0 text-sm bg-transparent border-0 focus:outline-none m-0"
                @blur="saveEdit"
                @keydown.enter="saveEdit"
                @keydown.esc="cancelEditing"
                @click.stop
              />

              <!-- Display Mode -->
              <div
                v-else
                class="truncate w-full"
                :title="formatCellValue(row[col.field], col.field)"
              >
                {{ formatCellValue(row[col.field], col.field) }}
              </div>
            </slot>
          </div>

          <!-- Row Actions Slot -->
          <div
            role="gridcell"
            class="inline-flex items-center px-4 text-sm flex-grow min-w-[120px]"
            :style="{ height: `${CELL_HEIGHT}px` }"
          >
            <slot name="row-actions" :row="row" />
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-if="!rows.length"
          class="flex items-center justify-center py-12 text-slate-500"
        >
          <slot name="empty-state"> No data available </slot>
        </div>
      </div>
    </div>
  </div>
</template>
